import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

// Redux
import { loginOperator, loginUser } from "../../store/actions/authSlice";

// App Components
import Copyright from "../copyrightFooter";
import LoadingBox from "../indicators/LoadingBox";

// Material UI
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 200, damping: 24 },
  },
};

const fadeScale = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
};

function Login() {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState({
    mother_user_id: undefined,
    username: "",
    password: "",
  });

  const [hasChosenType, setHasChosenType] = useState(false);
  const [chosenType, setChosenType] = useState(null);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (auth.auth?.isLoggedIn) {
      history.push("/central-delivery");
    }
  }, [auth.auth, history]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "success") {
      setSuccessMessage(
        "Sua senha foi redefinida com sucesso. Faca login com sua nova senha."
      );
      setOpenSuccessAlert(true);
      window.history.replaceState({}, document.title, "/login");
    }
  }, []);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleChosenType = (type) => {
    setHasChosenType(true);
    setChosenType(type);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setOpenErrorAlert(false);

    try {
      const data = new FormData(event.currentTarget);
      let result;

      if (chosenType === "operator") {
        const params = {
          mother_user_id: data.get("mother_user_id"),
          username: data.get("username"),
          password: data.get("password"),
        };
        result = await dispatch(loginOperator(params));
      } else {
        const params = {
          mother_user_id: data.get("username"),
          username: data.get("username"),
          password: data.get("password"),
        };
        result = await dispatch(loginUser(params));
      }

      if (result.error || !result.payload || result.payload.error) {
        throw new Error("Login failed");
      }

      history.push("/central-delivery", { fromLogin: true });
    } catch (e) {
      console.log(e);
      setErrorMessage("Senha ou usuario incorretos");
      setOpenErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (evt) => {
    const value = evt.target.value;
    setAccount({
      ...account,
      [evt.target.name]: value,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* ─── HERO ─── */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 6, md: 9 },
            pb: { xs: 12, md: 16 },
            background:
              "linear-gradient(155deg, #0f172a 0%, #1e293b 35%, #1e3a5f 70%, #1e3a8a 100%)",
          }}
        >
          {/* Grid pattern */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.03,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              zIndex: 0,
            }}
          />

          {/* Glow orbs */}
          <Box
            component={motion.div}
            animate={{ x: [0, 30, 0], y: [0, -15, 0] }}
            transition={{
              duration: 14,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)",
              top: -120,
              right: -80,
              zIndex: 0,
            }}
          />
          <Box
            component={motion.div}
            animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
            transition={{
              duration: 18,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 350,
              height: 350,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 65%)",
              bottom: -100,
              left: -50,
              zIndex: 0,
            }}
          />

          {/* Angled separator */}
          <Box
            sx={{
              position: "absolute",
              bottom: -1,
              left: 0,
              right: 0,
              lineHeight: 0,
            }}
          >
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style={{ width: "100%", height: 50, display: "block" }}
            >
              <path
                d="M0 80L1440 0V80H0Z"
                fill={theme.palette.background.default}
              />
            </svg>
          </Box>
        </Box>

        {/* ─── FORM CARD ─── */}
        <Container
          maxWidth="xs"
          sx={{
            mt: { xs: -6, md: -10 },
            pb: 8,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            }}
          >
            {/* Accent bar */}
            <Box
              sx={{
                height: 4,
                background: "linear-gradient(90deg, #2563eb 0%, #10b981 100%)",
              }}
            />

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              {/* Logo */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  component="img"
                  src="/images/logo.png"
                  alt="Slapie"
                  sx={{
                    width: { xs: 72, md: 88 },
                    height: { xs: 72, md: 88 },
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `3px solid ${theme.palette.divider}`,
                  }}
                />
              </Box>
              {loading ? (
                <Box sx={{ py: 6 }}>
                  <LoadingBox heightMetric="%" />
                  <Typography
                    align="center"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Entrando...
                  </Typography>
                </Box>
              ) : (
                <AnimatePresence mode="wait">
                  {hasChosenType ? (
                    <motion.div
                      key="form"
                      {...fadeScale}
                      transition={{ duration: 0.25 }}
                    >
                      {/* Form header */}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 3 }}
                      >
                        {chosenType === "operator" ? (
                          <HeadsetMicIcon sx={{ color: "#2563eb" }} />
                        ) : (
                          <AdminPanelSettingsIcon sx={{ color: "#2563eb" }} />
                        )}
                        <Typography variant="h6" fontWeight={600}>
                          {chosenType === "operator"
                            ? "Login de Operador"
                            : "Login de Proprietario"}
                        </Typography>
                      </Stack>

                      <Box component="form" noValidate onSubmit={handleSubmit}>
                        <Stack spacing={2.5}>
                          {chosenType === "operator" && (
                            <TextField
                              required
                              fullWidth
                              id="mother_user_id"
                              label="Identificador"
                              name="mother_user_id"
                              autoComplete="mother_user_id"
                              autoFocus
                              size="small"
                              value={account.mother_user_id}
                              onChange={handleChange}
                            />
                          )}
                          <TextField
                            required
                            fullWidth
                            id="username"
                            label="Usuario"
                            name="username"
                            autoComplete="username"
                            autoFocus={chosenType !== "operator"}
                            size="small"
                            value={account.username}
                            onChange={handleChange}
                          />
                          <TextField
                            required
                            fullWidth
                            name="password"
                            id="password"
                            label="Senha"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            size="small"
                            value={account.password}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? (
                                      <VisibilityIcon fontSize="small" />
                                    ) : (
                                      <VisibilityOffIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          endIcon={<LoginIcon />}
                          sx={{
                            mt: 3,
                            mb: 1,
                            py: 1.3,
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            background:
                              "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                            boxShadow: "0 4px 16px rgba(37,99,235,0.3)",
                            "&:hover": {
                              boxShadow: "0 8px 24px rgba(37,99,235,0.4)",
                            },
                          }}
                        >
                          Entrar
                        </Button>

                        {/* Alerts */}
                        {openSuccessAlert && (
                          <Alert
                            variant="filled"
                            severity="success"
                            action={
                              <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setOpenSuccessAlert(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                            sx={{ mt: 2, borderRadius: "8px" }}
                          >
                            {successMessage}
                          </Alert>
                        )}
                        {openErrorAlert && (
                          <Alert
                            variant="filled"
                            severity="error"
                            action={
                              <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setOpenErrorAlert(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                            sx={{ mt: 2, borderRadius: "8px" }}
                          >
                            {errorMessage}
                          </Alert>
                        )}

                        {/* Links */}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mt: 2.5 }}
                        >
                          <Link
                            href="#"
                            variant="body2"
                            underline="hover"
                            onClick={(e) => {
                              e.preventDefault();
                              setHasChosenType(false);
                              setOpenErrorAlert(false);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                              "&:hover": { color: theme.palette.primary.main },
                            }}
                          >
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                            Voltar
                          </Link>
                          <Link
                            href="/forgot-password"
                            variant="body2"
                            underline="hover"
                            onClick={(e) => {
                              e.preventDefault();
                              history.push("/forgot-password");
                            }}
                            sx={{
                              fontWeight: 500,
                              color: theme.palette.primary.main,
                            }}
                          >
                            Esqueci a senha
                          </Link>
                        </Stack>
                      </Box>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="choose"
                      {...fadeScale}
                      transition={{ duration: 0.25 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <PersonIcon sx={{ color: "#2563eb" }} />
                        <Typography variant="h6" fontWeight={600}>
                          Como deseja entrar?
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        Selecione o tipo de conta para continuar.
                      </Typography>

                      <Stack spacing={2}>
                        {/* Owner button */}
                        <Paper
                          elevation={0}
                          onClick={() => handleChosenType("admin")}
                          sx={{
                            p: 2.5,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: alpha("#2563eb", 0.4),
                              boxShadow: `0 4px 20px ${alpha("#2563eb", 0.1)}`,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "8px",
                                backgroundColor: alpha("#2563eb", 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <AdminPanelSettingsIcon
                                sx={{ color: "#2563eb", fontSize: 24 }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Proprietario
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Acesso completo ao sistema
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>

                        {/* Operator button */}
                        <Paper
                          elevation={0}
                          onClick={() => handleChosenType("operator")}
                          sx={{
                            p: 2.5,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: alpha("#10b981", 0.4),
                              boxShadow: `0 4px 20px ${alpha("#10b981", 0.1)}`,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "8px",
                                backgroundColor: alpha("#10b981", 0.08),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <HeadsetMicIcon
                                sx={{ color: "#10b981", fontSize: 24 }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                Operador
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Acesso com identificador da empresa
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Stack>

                      <Box
                        sx={{
                          mt: 3,
                          pt: 2.5,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          Nao tem uma conta?
                        </Typography>
                        <Link
                          href="/signup"
                          variant="body2"
                          underline="hover"
                          onClick={(e) => {
                            e.preventDefault();
                            history.push("/signup");
                          }}
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                          }}
                        >
                          Faca o cadastro
                        </Link>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </Box>
          </Paper>

          <Copyright sx={{ mt: 3 }} />
        </Container>
      </Box>
    </motion.div>
  );
}

export default Login;
